import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  RightOutlined,
  SunOutlined,
  TeamOutlined
} from "@ant-design/icons";
import {
  Button,
  Drawer,
  Empty,
  Modal,
  Space,
  Spin,
  Tooltip,
  message
} from "antd";
import { fetchSprNumberCatList } from "../number-cat/spr-number-cat-api";
import { fetchSprNumberSprList } from "../number-spr/spr-number-spr-api";
import keySrc from "@components/layout/assets/key.svg";
import stethoscopeSrc from "@components/layout/assets/stethoscope.svg";
import utensilsSrc from "@components/layout/assets/utensils.svg";
import {
  copySprNumberPriceUsl,
  deleteSprNumberPriceUsl,
  fetchSprNumberPrice,
  fetchSprNumberPricePrimAll,
  fetchSprNumberPriceUslList
} from "./spr-number-price-api";
import SprNumberPrice from "./spr-number-price";
import SprNumberPriceUsl from "./spr-number-price-usl";
import SprNumberPriceUslFilter from "./spr-number-price-usl-filter";
import { PrimKindIcon, primCountForVid, primKindUi } from "./spr-number-price-prim-ui";
import {
  IS_DAY_OPTIONS,
  PRIM_TABS,
  formatDate,
  formatMoney,
  formatRange,
  indexPrimByStr,
  labelByValue,
  matchesPrimFilter,
  normalizeUsl,
  normalizeZag,
  pick,
  toNumber
} from "./spr-number-price-utils";
import "./spr-number-price.css";
import "./spr-number-prices.css";

const DRAWER_WIDTH_KEY = "planOnline.sprNumberPrice.contentDrawer.width";
const COLLAPSED_GROUPS_KEY = "planOnline.sprNumberPrice.uslGroups.collapsed";
const MIN_WIDTH = 720;
const MAX_WIDTH = 1400;
const DEFAULT_WIDTH = 1040;
const SCREEN_MARGIN = 40;

const VID_RSH_ICONS = {
  0: keySrc,
  1: utensilsSrc,
  2: stethoscopeSrc
};

const USL_GROUPS = [
  { vidRsh: 0, label: "Проживание" },
  { vidRsh: 1, label: "Питание" },
  { vidRsh: 2, label: "Лечение" }
];

const BedIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden>
    <path
      d="M2 13V8.25A1.25 1.25 0 0 1 3.25 7h9.5A1.25 1.25 0 0 1 14 8.25V13M3.5 7V4.75A1.25 1.25 0 0 1 4.75 3.5h6.5A1.25 1.25 0 0 1 12.5 4.75V7M2 11.5h12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UslRateCard = ({ item, selected, primByVid, onEdit, onCopy, onDelete }) => {
  const title = item.name || item.uslName || `Строка №${item.idStr}`;
  const subtitle = item.uslName && item.uslName !== item.name ? item.uslName : null;
  const babyLabel =
    item.isBaby === 1 ? "Ребёнок" : item.isBaby === -1 ? "Не ребёнок" : null;
  const prims = PRIM_TABS.map((tab) => ({
    tab,
    count: primCountForVid(primByVid, tab.vid)
  })).filter((p) => p.count > 0);

  return (
    <div
      className={`spr-number-price-usl-card${selected ? " spr-number-price-usl-card--selected" : ""}`}
      onClick={() => onEdit(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          onEdit(item);
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
    >
      <div className="spr-number-price-usl-card__main">
        <h4 className="spr-number-price-usl-card__title">{title}</h4>
        {subtitle ? <div className="spr-number-price-usl-card__subtitle">{subtitle}</div> : null}
        <div className="spr-number-price-usl-card__meta">
          <Tooltip title="Срок">
            <span
              className="spr-number-price-usl-card__meta-item"
              aria-label={`Срок ${formatRange(item.srokMin, item.srokMax)}`}
            >
              <CalendarOutlined />
              {formatRange(item.srokMin, item.srokMax)}
            </span>
          </Tooltip>
          <Tooltip title="Клиентов">
            <span
              className="spr-number-price-usl-card__meta-item"
              aria-label={`Клиентов ${formatRange(item.cntClMin, item.cntClMax)}`}
            >
              <TeamOutlined />
              {formatRange(item.cntClMin, item.cntClMax)}
            </span>
          </Tooltip>
          <Tooltip title={labelByValue(IS_DAY_OPTIONS, item.isDay)}>
            <span
              className="spr-number-price-usl-card__meta-item"
              aria-label={labelByValue(IS_DAY_OPTIONS, item.isDay)}
            >
              {item.isDay === 1 ? <SunOutlined /> : <BedIcon />}
            </span>
          </Tooltip>
          {babyLabel ? <span>{babyLabel}</span> : null}
        </div>
      </div>
      {prims.length ? (
        <div className="spr-number-price-usl-card__prims" aria-label="Применимость">
          {prims.map(({ tab, count }) => (
            <Tooltip key={tab.vid} title={`${tab.shortLabel}: ${count}`}>
              <span
                className="spr-number-price-usl-card__prim spr-number-price-usl-card__prim--on"
                style={{ color: primKindUi(tab.vid).color }}
                aria-label={`${tab.shortLabel} ${count}`}
              >
                <PrimKindIcon vid={tab.vid} />
                {count}
              </span>
            </Tooltip>
          ))}
        </div>
      ) : null}
      <div className="spr-number-price-usl-card__prices">
        <Tooltip title="Будни">
          <span
            className="spr-number-price-usl-card__price spr-number-price-usl-card__price--weekday"
            aria-label={`Будни ${formatMoney(item.zena)} ₽`}
          >
            {formatMoney(item.zena)} ₽
          </span>
        </Tooltip>
        <Tooltip title="Выходные">
          <span
            className="spr-number-price-usl-card__price spr-number-price-usl-card__price--weekend"
            aria-label={`Выходные ${formatMoney(item.zenaHappy)} ₽`}
          >
            {formatMoney(item.zenaHappy)} ₽
          </span>
        </Tooltip>
      </div>
      <div
        className="spr-number-price-usl-card__actions"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Tooltip title="Копировать">
          <Button
            type="text"
            size="small"
            className="spr-number-price-usl-card__action"
            icon={<CopyOutlined />}
            aria-label="Копировать"
            onClick={() => onCopy(item)}
          />
        </Tooltip>
        <Tooltip title="Удалить">
          <Button
            type="text"
            size="small"
            danger
            className="spr-number-price-usl-card__action"
            icon={<DeleteOutlined />}
            aria-label="Удалить"
            onClick={() => onDelete(item)}
          />
        </Tooltip>
      </div>
    </div>
  );
};

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const loadCollapsedGroups = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(COLLAPSED_GROUPS_KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    return raw.map(Number).filter((n) => n === 0 || n === 1 || n === 2);
  } catch {
    return [];
  }
};

const saveCollapsedGroups = (ids) => {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer содержимого прайс-листа: строки услуг.
 */
const SprNumberPriceContent = ({ open, idNumberPrice, onClose, onZagChanged }) => {
  const [size, setSize] = useState(loadWidth);
  const [zag, setZag] = useState(null);
  const [uslRows, setUslRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsl, setLoadingUsl] = useState(false);
  const [selectedUslId, setSelectedUslId] = useState(null);
  const [nameFilter, setNameFilter] = useState("");
  const [primFilters, setPrimFilters] = useState({});
  const [primByStr, setPrimByStr] = useState({});
  const [primOptionsByVid, setPrimOptionsByVid] = useState({});
  const [zagEditOpen, setZagEditOpen] = useState(false);
  const [uslDrawer, setUslDrawer] = useState({ open: false, isNew: false, idStr: null });
  const [collapsedVidRsh, setCollapsedVidRsh] = useState(loadCollapsedGroups);

  useEffect(() => {
    saveCollapsedGroups(collapsedVidRsh);
  }, [collapsedVidRsh]);

  const setGroupCollapsed = (vidRsh, collapsed) => {
    setCollapsedVidRsh((prev) => {
      const isCollapsed = prev.includes(vidRsh);
      if (collapsed === isCollapsed) return prev;
      return collapsed ? [...prev, vidRsh] : prev.filter((id) => id !== vidRsh);
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const loadZag = useCallback(() => {
    if (idNumberPrice == null) {
      setZag(null);
      return Promise.resolve();
    }
    return fetchSprNumberPrice(idNumberPrice)
      .then((row) => setZag(normalizeZag(row)))
      .catch((e) => {
        message.error(e.message || "Не удалось загрузить прайс");
        setZag(null);
      });
  }, [idNumberPrice]);

  const loadPrimLookups = useCallback(() => {
    return Promise.all(
      PRIM_TABS.map(async (tab) => {
        if (tab.source === "number-cat") {
          const data = await fetchSprNumberCatList();
          const opts = (Array.isArray(data) ? data : [])
            .map((row) => {
              const id = toNumber(pick(row, "idNumberCat", "IdNumberCat"));
              const name = pick(row, "name", "Name") ?? "";
              if (id == null) return null;
              return { value: id, label: name || `#${id}` };
            })
            .filter(Boolean)
            .sort((a, b) => a.label.localeCompare(b.label, "ru"));
          return [tab.vid, opts];
        }
        const data = await fetchSprNumberSprList(tab.sprVid);
        const items = (Array.isArray(data) ? data : [])
          .map((row) => {
            const id = toNumber(pick(row, "id", "Id"));
            const name = pick(row, "name", "Name") ?? "";
            if (id == null) return null;
            return { value: id, label: name || `#${id}` };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));
        const opts = tab.zeroLabel ? [{ value: 0, label: tab.zeroLabel }, ...items] : items;
        return [tab.vid, opts];
      })
    ).then((entries) => Object.fromEntries(entries));
  }, []);

  const loadUsl = useCallback(() => {
    if (idNumberPrice == null) {
      setUslRows([]);
      setSelectedUslId(null);
      setPrimByStr({});
      return;
    }
    setLoadingUsl(true);
    Promise.all([fetchSprNumberPriceUslList(idNumberPrice), fetchSprNumberPricePrimAll(idNumberPrice)])
      .then(([data, primRows]) => {
        const rows = (Array.isArray(data) ? data : [])
          .map(normalizeUsl)
          .filter((r) => r.idStr != null);
        setUslRows(rows);
        setPrimByStr(indexPrimByStr(primRows));
        setSelectedUslId((prev) =>
          prev != null && rows.some((r) => r.idStr === prev) ? prev : null
        );
      })
      .catch((e) => {
        message.error(e.message || "Не удалось загрузить строки прайса");
        setUslRows([]);
        setPrimByStr({});
        setSelectedUslId(null);
      })
      .finally(() => setLoadingUsl(false));
  }, [idNumberPrice]);

  useEffect(() => {
    if (!open || idNumberPrice == null) {
      setZag(null);
      setUslRows([]);
      setSelectedUslId(null);
      setNameFilter("");
      setPrimFilters({});
      setPrimByStr({});
      setZagEditOpen(false);
      setUslDrawer({ open: false, isNew: false, idStr: null });
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([loadZag(), Promise.resolve(loadUsl()), loadPrimLookups()])
      .then(([, , opts]) => {
        if (!cancelled && opts) setPrimOptionsByVid(opts);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, idNumberPrice, loadZag, loadUsl, loadPrimLookups]);

  const filteredUslRows = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    const primEntries = Object.entries(primFilters).filter(([, id]) => id != null);
    if (!q && primEntries.length === 0) return uslRows;
    return uslRows.filter((r) => {
      if (q) {
        const nameOk =
          String(r.name || "")
            .toLowerCase()
            .includes(q) ||
          String(r.uslName || "")
            .toLowerCase()
            .includes(q);
        if (!nameOk) return false;
      }
      for (const [vid, id] of primEntries) {
        if (!matchesPrimFilter(primByStr[r.idStr]?.[Number(vid)], id)) return false;
      }
      return true;
    });
  }, [uslRows, nameFilter, primFilters, primByStr]);

  const groupedUslRows = useMemo(
    () =>
      USL_GROUPS.map((group) => ({
        ...group,
        rows: filteredUslRows.filter((r) => (r.vidRsh ?? 0) === group.vidRsh)
      })).filter((group) => group.rows.length > 0),
    [filteredUslRows]
  );

  const openUslCreate = () => setUslDrawer({ open: true, isNew: true, idStr: null });
  const openUslEdit = (record) => {
    if (record?.idStr == null) return;
    setSelectedUslId(record.idStr);
    setUslDrawer({ open: true, isNew: false, idStr: record.idStr });
  };
  const closeUslDrawer = () => setUslDrawer({ open: false, isNew: false, idStr: null });

  const onUslSaved = (saved) => {
    const newId = toNumber(saved?.idStr ?? saved?.IdStr);
    loadUsl();
    if (newId != null) setSelectedUslId(newId);
    if (uslDrawer.isNew && newId != null) {
      closeUslDrawer();
      setTimeout(() => setUslDrawer({ open: true, isNew: false, idStr: newId }), 0);
    } else {
      closeUslDrawer();
    }
  };

  const confirmUslDelete = (row) => {
    if (idNumberPrice == null || row?.idStr == null) return;
    const idStr = row.idStr;
    Modal.confirm({
      title: "Удалить строку?",
      content: row.name || row.uslName || `№${idStr}`,
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: () =>
        deleteSprNumberPriceUsl(idNumberPrice, idStr)
          .then(() => {
            message.success("Удалено");
            if (uslDrawer.idStr === idStr) closeUslDrawer();
            if (selectedUslId === idStr) setSelectedUslId(null);
            loadUsl();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const copyUsl = (row) => {
    if (idNumberPrice == null || row?.idStr == null) return;
    copySprNumberPriceUsl(idNumberPrice, row.idStr)
      .then((created) => {
        message.success("Скопировано");
        const newId = toNumber(created?.idStr ?? created?.IdStr);
        loadUsl();
        if (newId != null) {
          setSelectedUslId(newId);
          setUslDrawer({ open: true, isNew: false, idStr: newId });
        }
      })
      .catch((e) => message.error(e.message || "Не удалось скопировать"));
  };

  const onZagSaved = (saved) => {
    loadZag();
    onZagChanged?.(saved);
    setZagEditOpen(false);
  };

  const title = zag
    ? `${zag.name || "Прайс-лист"} №${zag.idNumberPrice} (${formatDate(zag.dateBeg)} — ${formatDate(zag.dateEnd)})`
    : idNumberPrice != null
      ? `Прайс-лист №${idNumberPrice}`
      : "Прайс-лист";

  return (
    <>
      <Drawer
        placement="right"
        title={title}
        size={size}
        maxSize={maxAllowedWidth()}
        open={open}
        onClose={onClose}
        destroyOnClose
        closable={false}
        rootClassName="spr-number-price-content-drawer"
        styles={{
          header: { padding: "8px 12px" },
          body: { display: "flex", flexDirection: "column", padding: "6px 8px" }
        }}
        resizable={{
          onResize,
          onResizeEnd
        }}
        extra={
          <div className="spr-number-price-content-drawer__extra">
            <SprNumberPriceUslFilter
              name={nameFilter}
              onNameChange={setNameFilter}
              primFilters={primFilters}
              onPrimFiltersChange={setPrimFilters}
              optionsByVid={primOptionsByVid}
            />
            <Space size={2}>
              <Tooltip title="Добавить">
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  aria-label="Добавить"
                  onClick={openUslCreate}
                />
              </Tooltip>
              <Tooltip title="Изменить">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  aria-label="Изменить"
                  onClick={() => setZagEditOpen(true)}
                />
              </Tooltip>
              <Tooltip title="Закрыть">
                <Button
                  type="text"
                  size="small"
                  className="spr-number-price-drawer__close"
                  icon={<CloseOutlined />}
                  aria-label="Закрыть"
                  onClick={onClose}
                />
              </Tooltip>
            </Space>
          </div>
        }
      >
        {loading && !uslRows.length ? (
          <div className="spr-number-prices__center">
            <Spin tip="Загрузка..." />
          </div>
        ) : (
          <div className="spr-number-price-content">
            <div className="spr-number-prices__usl">
              <div className="spr-number-prices__cards-body">
                <Spin spinning={loadingUsl}>
                  {groupedUslRows.length ? (
                    <div className="spr-number-price-usl-groups">
                      {groupedUslRows.map((group) => {
                        const collapsed = collapsedVidRsh.includes(group.vidRsh);
                        return (
                          <section
                            key={group.vidRsh}
                            className={`spr-number-price-usl-group${collapsed ? " spr-number-price-usl-group--collapsed" : ""}`}
                          >
                            <button
                              type="button"
                              className={`spr-number-price-usl-group__toggle spr-number-price-usl-group__toggle--${group.vidRsh}`}
                              aria-expanded={!collapsed}
                              onClick={() => setGroupCollapsed(group.vidRsh, !collapsed)}
                            >
                              <RightOutlined
                                className="spr-number-price-usl-group__chevron"
                                rotate={collapsed ? 0 : 90}
                              />
                              <span
                                className="spr-number-price-usl-group__icon"
                                style={{ "--kind-icon": `url("${VID_RSH_ICONS[group.vidRsh]}")` }}
                                aria-hidden
                              />
                              <span className="spr-number-price-usl-group__label">{group.label}</span>
                              <span className="spr-number-price-usl-group__count">{group.rows.length}</span>
                            </button>
                            {collapsed ? null : (
                              <div className="spr-number-price-usl-cards">
                                {group.rows.map((item) => (
                                  <UslRateCard
                                    key={item.idStr}
                                    item={item}
                                    selected={selectedUslId != null && item.idStr === selectedUslId}
                                    primByVid={primByStr[item.idStr]}
                                    onEdit={openUslEdit}
                                    onCopy={copyUsl}
                                    onDelete={confirmUslDelete}
                                  />
                                ))}
                              </div>
                            )}
                          </section>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty
                      className="spr-number-price-usl-cards__empty"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        nameFilter.trim() || Object.values(primFilters).some((id) => id != null)
                          ? "Нет строк по фильтру"
                          : "Нет строк прайса"
                      }
                    />
                  )}
                </Spin>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <SprNumberPrice
        open={zagEditOpen}
        idNumberPrice={idNumberPrice}
        isNew={false}
        onClose={() => setZagEditOpen(false)}
        onSaved={onZagSaved}
      />
      <SprNumberPriceUsl
        open={uslDrawer.open}
        idNumberPrice={idNumberPrice}
        idStr={uslDrawer.idStr}
        isNew={uslDrawer.isNew}
        onClose={closeUslDrawer}
        onSaved={onUslSaved}
        onPrimChanged={loadUsl}
      />
    </>
  );
};

export default SprNumberPriceContent;
